import { Component, computed, effect, EventEmitter, inject, Injector, Input, OnDestroy, OnInit, input, Output, signal } from '@angular/core';
import { CallTavusOverlayComponent } from 'src/app/components/shared/call-tavus-overlay/call-tavus-overlay.component';
import { CallOverlayService } from 'src/app/components/shared/call-tavus-overlay/call-overlay.service';
import { ControlService } from 'src/app/pages/learner/call/library/control.service';
import { RealtimeService } from 'src/app/pages/learner/call/library/realtime/realtime.service';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';
import { UserVideoService } from 'src/app/pages/learner/call/library/user-video.service';
import { DeviceSettingsService } from 'src/app/pages/learner/call/library/device-settings.service';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';
import { PathwayRoleplayService } from 'src/app/pages/learner/call/call-pathway-roleplay/services/pathway-roleplay.service';
import { ResultsPathwayRoleplayComponent } from '../../../results/results-pathway-roleplay/results-pathway-roleplay.component';
import { PathwayCallFooterComponent } from 'src/app/pages/learner/call/call-pathway/call-footer/call-footer';
import { PathwayCallControlsComponent } from 'src/app/pages/learner/call/call-pathway/call-controls/call-controls';
import { PathwayRoleplayFooterService } from 'src/app/pages/learner/call/call-pathway-roleplay/services/pathway-roleplay-footer.service';
import { TranslocoService } from '@jsverse/transloco';
import { PathwayStepType } from 'src/app/utils/enums/learner/pathway.enum';

@Component({
  selector: 'app-call-roleplay-results',
  standalone: true,
  imports: [CallTavusOverlayComponent, ResultsPathwayRoleplayComponent, PathwayCallFooterComponent, PathwayCallControlsComponent],
  providers: [CallOverlayService],
  templateUrl: './call-roleplay-results.component.html',
  styleUrls: ['./call-roleplay-results.component.scss'],
})
export class CallRoleplayResultsComponent implements OnInit, OnDestroy {
  readonly PathwayStepType = PathwayStepType;
  readonly callUuid = input<string>('');
  readonly callType = input<string>('');
  readonly stepType = input<string>('');
  readonly moduleAssignmentUuid = input<string>('');
  readonly realtimeCallData = input<ICallRealtime | null>(null);
  readonly captionsOn = input<boolean>(false);
  readonly captionText = input<string>('');
  readonly cardWidthPx = signal(0);

  @Input() realtimeService: RealtimeService | undefined;
  @Input() controlService: ControlService | undefined;
  @Input() callStore: CallStoreService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() endedEarly: boolean = false;
  @Output() returnToPathway = new EventEmitter<void>();
  @Output() confirmAction = new EventEmitter<void>();

  private readonly injector = inject(Injector);

  protected get userDisplayName(): string {
    return localStorage.getItem('user_first_name')?.trim() || '';
  }

  protected get avatarIsSpeaking(): boolean {
    return this.callStore?.getIsAvatarSpeaking() ?? false;
  }

  protected get userIsSpeaking(): boolean {
    return !this.avatarIsSpeaking && (this.callStore?.getIsMicOn() ?? false);
  }

  protected get showUserInitials(): boolean {
    return (
      (this.callStore?.getIsPaused() ?? false) ||
      (this.deviceSettingsService?.isCameraUnavailable ?? false) ||
      !(this.controlService?.isVideoOn ?? true)
    );
  }

  readonly startCallData = computed(() => {
    const data = this.realtimeCallData();
    if (!data) return null;
    return {
      controlParams: {
        callUuid: this.callUuid(),
        callType: this.callType(),
        moduleAssignmentUuid: this.moduleAssignmentUuid(),
      },
      realtimeCallData: data,
    };
  });

  readonly ctaLabel = computed(() =>
    this.translocoService.translate(this.footerService.actionLabel())
  );

  constructor(
    readonly callOverlayService: CallOverlayService,
    public readonly roleplayService: PathwayRoleplayService,
    readonly footerService: PathwayRoleplayFooterService,
    private readonly translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    const {
      realtimeService,
      controlService,
      callStore,
      userVideoService,
      deviceSettingsService,
    } = this;
    if (!realtimeService || !controlService || !callStore) return;

    this.callOverlayService.init({
      realtimeService,
      controlService,
      callStore,
      userVideoService,
      deviceSettingsService,
    });

    effect(
      () => {
        const params = this.startCallData();
        if (!params) return;
        this.callOverlayService.startCall(params);
      },
      { injector: this.injector },
    );


  }

  ngOnDestroy(): void {
    this.callOverlayService.destroy();
  }
}
